import styled, { css } from 'styled-components';
import {
    ANIMATION_CURVE,
    ANIMATION_DURATION,
    Disableable,
    FOCUS_OUTLINE_STYLE,
    getSpacingToken,
    getTextTokenPreset,
    getToken,
    Size,
} from '@optimus-web/common';
import {
    getBackgroundColor,
    getBackgroundColorActive,
    getBackgroundColorHover,
    getBorderColor,
    getBorderColorActive,
    getBorderColorHover,
} from './getRadioStyles';

export interface RadioElementProps {
    inputHidden?: boolean;
    $disabled?: boolean;
    size?: Exclude<Size, 'extraLarge'>;
}

export const RadioElement = styled.div<RadioElementProps>`
    flex-shrink: 0;
    display: inline-block;
    position: relative;
    width: ${({ size }) => (size === 'large' ? getToken(`sizing300`) : getToken('sizing200'))}px;
    height: ${({ size }) => (size === 'large' ? getToken(`sizing300`) : getToken('sizing200'))}px;
    margin-right: ${getSpacingToken('150')}px;
    ${({ inputHidden }) => inputHidden && 'display: none;'}
`;

export const RadioBackgroundElement = styled.div`
    display: inline-block;
    position: absolute;
    width: 100%;
    height: 100%;
    box-sizing: border-box;
`;

export interface RadioCircleProps {
    checked?: boolean;
    disabled?: boolean;
}

export const RadioOuterCircleElement = styled.div<RadioCircleProps>`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    box-sizing: border-box;
    border-width: ${getToken('borderWidth100')}px;
    border-style: solid;
    border-radius: ${getToken('borderRadiusRound')};
    transition: border-color 120ms ${ANIMATION_CURVE.default};
    border-color: ${getBorderColor};
    background-color: ${getBackgroundColor};

    &:hover {
        border-color: ${getBorderColorHover};
        background-color: ${getBackgroundColorHover};
    }

    &:active {
        border-color: ${getBorderColorActive};
        background-color: ${getBackgroundColorActive};
    }
`;

export const RadioInnerCircleElement = styled.div<RadioCircleProps>`
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    border-radius: ${getToken('borderRadiusRound')};
    background-color: ${getToken('backgroundStaticFlat')};
    transform: scale(0, 0);
    transition:
        transform ${ANIMATION_DURATION.default}ms ${ANIMATION_CURVE.default},
        background-color ${ANIMATION_DURATION.default}ms ${ANIMATION_CURVE.default};

    ${({ disabled, checked }) =>
        disabled &&
        checked &&
        css`
            border-color: ${getToken('borderDisabled')};
        `}

    ${({ checked }) =>
        checked &&
        css`
            transform: scale(0.4);
        `}
`;

export interface RadioInputElementProps {
    onChange: (e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
    checked?: boolean;
    disabled?: boolean;
}

export const RadioInputElement = styled.input<RadioInputElementProps>`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    margin: 0;
    padding: 0;
    opacity: 0;
    z-index: 1;
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};

    &:focus-visible + ${RadioBackgroundElement} > ${RadioOuterCircleElement} {
        border-radius: ${getToken('borderRadiusRound')};
        ${FOCUS_OUTLINE_STYLE}
    }
`;

export interface RadioButtonElementProps {
    checked?: boolean;
    disabled?: boolean;
}

export const RadioButtonElement = styled.span<RadioButtonElementProps>`
    display: inline-flex;
    align-items: center;
    padding: ${getSpacingToken('50')}px 0;
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    width: fit-content;
`;

export interface RadioLabelElementProps extends Disableable {
    htmlFor?: string;
    checked?: boolean;
    size?: Exclude<Size, 'extraLarge'>;
}

export const RadioLabelElement = styled.label<RadioLabelElementProps>`
    ${({ size }) => (size === 'small' ? getTextTokenPreset('bodyMedium') : getTextTokenPreset('bodyLarge'))}
    color: ${(props) => (props.disabled ? getToken('textDisabled') : getToken('textStaticPrimary'))};
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
`;

export const RadioGroupElement = styled.div`
    display: flex;
    flex-direction: column;
    border: none;
    margin: 0;
    padding: 0;
    min-width: 0;
    min-inline-size: min-content;
`;

export const RadioButtonGroupContainer = styled.span`
    display: inline-flex;
    flex-direction: column;
    margin: ${getSpacingToken('100')}px 0;
    width: fit-content;
`;
